import { useEffect, useState } from "react";
import WSPayment from "../../../services/remote/payment/WSPayment";
import { StorePack } from "../../../entity/StorePack";
import "./PaymentScreen.css";
import { useTranslation } from "react-i18next";
import WSInvoice from "../../../services/remote/invoice/WSInvoice";
import { useSelector } from "react-redux";
import { selectStore } from "../../../store/selectors/StoreSelector";
import { Invoice } from "../../../entity/Invoice";

export default function PaymentScreen() {
  const [packs, setPacks] = useState<StorePack[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);

  const store = useSelector(selectStore);

  const { t } = useTranslation();

  const fetchPacks = async () => {
    try {
      const packs = await WSPayment.getInstance().getStorePacks();

      setPacks(packs.sort((a, b) => a.price - b.price));
    } catch (error) {
      console.error("Error fetching packs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoice = async () => {
    try {
      const invoice = await WSInvoice.getInvoicesByStoreId(store!.id);
      setInvoices(invoice);
    } catch (error) {
      console.error("Error fetching invoice:", error);
    }
  };

  useEffect(() => {
    fetchPacks();
    if (store) {
      fetchInvoice();
    }
  }, [store]);

  const handleSelect = (pack: StorePack) => {
    if (!store) return;

    const separator = pack.urlRedirect.includes("?") ? "&" : "?";

    const url =
      `${pack.urlRedirect}` +
      `${separator}checkout[custom][storeId]=${encodeURIComponent(store.id)}` +
      `&checkout[custom][packId]=${encodeURIComponent(pack.id!)}`;

    window.location.href = url;
  };

  return (
    <div className="pay-root">
      <div className="pay-header">
        <div className="pay-header-eyebrow">💳 {t("payment.subscription")}</div>
        <h1>{t("payment.choosePlan")}</h1>
        <p>{t("payment.oneTimePayment")}</p>
      </div>
      <div className="pay-grid">
        {loading
          ? Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="pack-card skeleton">
                <div
                  className="skel-block"
                  style={{ height: 12, width: "60%", marginBottom: 12 }}
                />
                <div
                  className="skel-block"
                  style={{ height: 36, width: "40%", marginBottom: 10 }}
                />
                <div
                  className="skel-block"
                  style={{ height: 40, width: "100%", marginBottom: 14 }}
                />
                <div
                  className="skel-block"
                  style={{ height: 12, width: "85%", marginBottom: 8 }}
                />
                <div
                  className="skel-block"
                  style={{ height: 12, width: "90%", marginBottom: 16 }}
                />
                <div
                  className="skel-block"
                  style={{ height: 38, width: "100%", borderRadius: 8 }}
                />
              </div>
            ))
          : packs.map((pack, i) => {
              const featured = i === 2;
              return (
                <div
                  key={pack.id}
                  className={`pack-card${featured ? " featured" : ""}`}
                >
                  {featured && (
                    <span className="badge-popular">
                      {t("payment.popular")}
                    </span>
                  )}
                  <p className="pack-name">{pack.name}</p>
                  <div className="pack-price-row">
                    <span className="pack-currency">$</span>
                    <span className="pack-amount">{pack.price}</span>
                    <span className="pack-period">/ {t("payment.month")}</span>
                  </div>
                  <p className="pack-description">{pack.description}</p>
                  <ul className="pack-features">
                    {pack.features.map((f) => (
                      <li key={f}>
                        <span className="check-icon">✓</span>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    className={`pack-cta ${featured ? "primary" : "secondary"}`}
                    onClick={() => handleSelect(pack)}
                  >
                    {t("payment.getPlan", { plan: pack.name })}
                  </button>
                </div>
              );
            })}
      </div>
      <hr className="pack-divider" />

      <div className="invoices-section">
        <div className="invoices-header">
          <h2>{t("payment.invoices")}</h2>
          <span className="invoices-count">
            {invoices.length} {t("payment.invoices")}
          </span>
        </div>
        <div className="invoices-table-wrap">
          <table className="invoices-table">
            <thead>
              <tr>
                <th style={{ width: "35%" }}>{t("payment.invoice")}</th>
                <th style={{ width: "22%" }}>{t("payment.plan")}</th>
                <th style={{ width: "18%" }}>{t("payment.amount")}</th>
                <th style={{ width: "13%" }}>{t("payment.status")}</th>
                <th style={{ width: "12%" }}>{t("payment.date")}</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td className="td-id">#{invoice.id}</td>
                  <td>
                    {packs.find((p) => p.id === invoice.storePackId)?.name ??
                      t("payment.unknown")}
                  </td>
                  <td>${invoice.amount}</td>
                  <td>
                    <span
                      className={`status-pill ${invoice.status.toLowerCase()}`}
                    >
                      <span className="status-dot" />
                      {invoice.status}
                    </span>
                  </td>
                  <td className="td-muted">
                    {invoice.createdAt
                      ? new Date(invoice.createdAt).toLocaleDateString()
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
